import React, { FC, useState, useMemo, ComponentProps, useImperativeHandle } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';

import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { useStyles } from './prospect-information.style';
import { useProspectInformation } from './prospect-information.hook';
import Form, { Item } from 'src/app/common/components/Form';
import { ProspectCategoryPicker } from '../../../add-prospect/components/prospect-category-picker/prospect-category-picker.component';
import { ProspectDetail } from 'src/app/modules/Prospect/types/prospect-types';
import { is } from 'date-fns/locale';
import { ReferredBy } from '../../../components/referred-by/referred-by.component';
import { CategoryHistoryComponent } from '../category-history/category-history.component';
import { ProspectCategoryProps } from 'src/app/common/components/prospect-category/prospect-category.component';
import { DeleteProspectDialog } from '../delete-prospect-dialog/delete-prospect-dialog.component';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

type PageProps = ParamsProps &
  RouteComponentProps & {
    prospectDetail: ProspectDetail;
    readOnly?: boolean;
  } & ComponentProps<'base'>;

type InputItemProps = {
  name: string;
  label: string;
  defaultValue?: string | number | boolean;
  required?: boolean;
  isSelect?: boolean;
  selectOptions?: { label: string; value: string | boolean }[];
  readonly?: boolean;
  filedError?: {
    error?: string;
  };
};

const InputItem = (props: InputItemProps) => {
  const { name, label, defaultValue, required, isSelect, selectOptions, readonly, filedError } = props;
  const { classes } = useStyles();
  const styles = useMemo(() => classes, [classes]);
  return (
    <Item name={name} hideError>
      <TextField
        label={label}
        variant="outlined"
        defaultValue={defaultValue}
        required={required}
        className={styles.input}
        size="medium"
        select={isSelect}
        SelectProps={{
          size: 'medium',
        }}
        inputProps={{
          readOnly: readonly,
        }}
        error={Boolean(filedError)}
        helperText={filedError?.error ?? ''}
      >
        {isSelect &&
          selectOptions &&
          selectOptions.map((item, index) => (
            <MenuItem key={`${item.value}_${index}`} value={item.value.toString()}>{`${item.label}`}</MenuItem>
          ))}
      </TextField>
    </Item>
  );
};

export const ProspectInformationPage = React.forwardRef((props: PageProps, ref) => {
  const { prospectDetail, readOnly } = props;
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  // style
  const { classes } = useStyles();
  const styles = useMemo(() => classes, [classes]);

  // custom-hook
  const {
    form,
    formModel,
    selectionPools,
    showReferrby,
    selectedReferrBy,
    region,
    cityList,
    requiredFields,
    filedErrors,
    showDeleteDialog,
    selectedCategory,
    onSelectReferrBy,
    setShowReferrby,
    onFiledChange,
    onCategoryChange,
    onDeleteDialogClose,
    deleteProspect,
    submit,
    onReferredByClick,
    onBirthDateChange,
    resetFormStatus,
  } = useProspectInformation({
    prospectDetail: prospectDetail,
  });

  const submitForm = () => {
    submit();
  };

  const resetForm = () => {
    form.resetFields();
    resetFormStatus();
  };

  useImperativeHandle(ref, () => ({
    submitForm: submitForm,
    resetForm: resetForm,
  }));

  return (
    <div>
      <Form initialValues={formModel} form={form} onFieldsChange={onFiledChange}>
        <div className={styles.sectionBox}>
          <div className={styles.sectionTitle}>
            <span>{Translation('prospect.personal_infomation')}</span>
          </div>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6}>
              <InputItem
                name={'firstName'}
                label={Translation('prospect.first_name')}
                defaultValue={formModel.firstName}
                readonly={readOnly}
                required={requiredFields.firstName}
                filedError={filedErrors['firstName']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name={'lastName'}
                label={Translation('prospect.last_name')}
                defaultValue={formModel.lastName}
                readonly={readOnly}
                required={requiredFields.lastName}
                filedError={filedErrors['lastName']}
              />
            </Grid>
          </Grid>
          <div className={styles.categoryPicker}>
            <div className={styles.categoryTitle}>
              <span>{`${Translation('prospect.category')} *`}</span>
            </div>
            <ProspectCategoryPicker
              onChanage={onCategoryChange}
              defaultCategory={formModel.prospectCategory}
              editable={!readOnly}
              value={selectedCategory}
              removeNoneType
            />
          </div>

          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6}>
              <InputItem
                name="gender"
                label={Translation('salesLdGender')}
                isSelect
                selectOptions={selectionPools.genderPools}
                readonly={readOnly}
                defaultValue={formModel.gender}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label={Translation('prospect.birth_of_date')}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    size: 'medium',
                    fullWidth: true,
                  },
                }}
                key={String(readOnly)}
                readOnly={readOnly}
                onChange={onBirthDateChange}
                defaultValue={formModel.birthDate ? dayjs(formModel.birthDate) : undefined}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="maritalStatus"
                label={Translation('prospect.marital_status')}
                isSelect
                selectOptions={selectionPools.maritalStatusPools}
                readonly={readOnly}
                defaultValue={formModel.maritalStatus}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name={'occupation'}
                label={Translation('prospect.occupation')}
                readonly={readOnly}
                defaultValue={formModel.occupation}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name={'company'}
                label={Translation('prospect.company_of_employment')}
                readonly={readOnly}
                defaultValue={formModel.company}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name={'workPosition'}
                label={Translation('prospect.work_position')}
                readonly={readOnly}
                defaultValue={formModel.workPosition}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name={'income'}
                label={Translation('prospect.income')}
                readonly={readOnly}
                defaultValue={formModel.income}
                filedError={filedErrors['income']}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name={'language'}
                label={Translation('prospect.preferred_language')}
                isSelect
                selectOptions={selectionPools.languagePools}
                readonly={readOnly}
                defaultValue={formModel.language}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="race"
                label={Translation('prospect.race')}
                isSelect
                selectOptions={selectionPools.racePools}
                readonly={readOnly}
                defaultValue={formModel.race}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="nationality"
                label={Translation('prospect.nationality')}
                readonly={readOnly}
                defaultValue={formModel.nationality}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="permanentResident"
                label={Translation('prospect.permanent_resident')}
                isSelect
                selectOptions={selectionPools.permanentResidentPools}
                readonly={readOnly}
                defaultValue={formModel.permanentResident}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="education"
                label={Translation('prospect.education')}
                readonly={readOnly}
                defaultValue={formModel.education}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="smokeHabit"
                label={Translation('prospect.smoker_indicator')}
                isSelect
                selectOptions={selectionPools.smokerIndicatorPools}
                readonly={readOnly}
                defaultValue={formModel.smokeHabit}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="salutation"
                label={Translation('prospect.salutation')}
                isSelect
                selectOptions={selectionPools.salutationPools}
                readonly={readOnly}
                defaultValue={formModel.salutation}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="countryNationalId"
                label={Translation('prospect.country_national_id')}
                readonly={readOnly}
                defaultValue={formModel.countryNationalId}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="chinaId"
                label={Translation('prospect.china_id')}
                readonly={readOnly}
                defaultValue={formModel.chinaId}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name="passportNumber"
                label={Translation('prospect.passport_number')}
                readonly={readOnly}
                defaultValue={formModel.passportNumber}
              />
            </Grid>

            <Grid item xs={6}>
              <InputItem
                name={'notes'}
                label={Translation('prospect.notes')}
                readonly={readOnly}
                defaultValue={formModel.notes}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={Translation('prospect.referred_by')}
                variant="outlined"
                className={styles.input}
                size="medium"
                onClick={() => {
                  onReferredByClick(readOnly);
                }}
                InputProps={{
                  readOnly: true,
                }}
                required={requiredFields.referredBy}
                value={selectedReferrBy?.displayName ?? ''}
                error={filedErrors['referredBy']}
                helperText={filedErrors['referredBy']?.error ?? ''}
              ></TextField>
            </Grid>
          </Grid>
        </div>
        <div className={styles.sectionBox}>
          <div className={styles.sectionTitle}>
            <span>{Translation('prospect.contact_information')}</span>
          </div>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6}>
              <InputItem
                name="regionCode"
                label={Translation('prospect.country_region_code')}
                isSelect
                selectOptions={region}
                required={requiredFields.regionCode}
                readonly={readOnly}
                defaultValue={formModel.regionCode}
                filedError={filedErrors['regionCode']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="phoneNumber"
                label={Translation('prospect.phone_number')}
                required={requiredFields.phoneNumber}
                readonly={readOnly}
                defaultValue={formModel.phoneNumber}
                filedError={filedErrors['phoneNumber']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="secondPhoneNumber"
                label={Translation('prospect.second_phone_number')}
                readonly={readOnly}
                defaultValue={formModel.secondPhoneNumber}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="email"
                label={Translation('prospect.email_address')}
                required={requiredFields.email}
                readonly={readOnly}
                defaultValue={formModel.email}
                filedError={filedErrors['email']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="secondEmail"
                label={Translation('prospect.second_email_address')}
                readonly={readOnly}
                defaultValue={formModel.secondEmail}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="fbProfile"
                label={Translation('prospect.facebook_profile')}
                readonly={readOnly}
                defaultValue={formModel.fbProfile}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="wechatId"
                label={Translation('prospect.wechat_id')}
                readonly={readOnly}
                defaultValue={formModel.wechatId}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="whatsApp"
                label={Translation('prospect.whatsApp')}
                readonly={readOnly}
                defaultValue={formModel.whatsApp}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="address"
                label={Translation('prospect.address')}
                readonly={readOnly}
                defaultValue={formModel.address}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="state"
                label={Translation('prospect.state')}
                isSelect
                selectOptions={selectionPools.statePools}
                readonly={readOnly}
                defaultValue={formModel.state}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="city"
                label={Translation('prospect.city')}
                isSelect
                selectOptions={cityList}
                readonly={readOnly}
                defaultValue={formModel.city}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="optedOutContactStatus"
                label={Translation('prospect.opt_out_y_n')}
                isSelect
                selectOptions={selectionPools.optedOutContactStatusPools}
                readonly={readOnly}
                defaultValue={formModel.optedOutContactStatus}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="zipCode"
                label={Translation('prospect.zipCode')}
                readonly={readOnly}
                defaultValue={formModel.zipCode}
              />
            </Grid>
          </Grid>
        </div>
        <div className={styles.sectionBox}>
          <div className={styles.sectionTitle}>
            <span>{Translation('prospect.family_information')}</span>
          </div>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6}>
              <InputItem
                name="totalChild"
                label={Translation('prospect.number_of_children')}
                defaultValue={formModel.totalChild}
                readonly={readOnly}
                filedError={filedErrors['totalChild']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="childrenDetails"
                label={Translation('prospect.children_details')}
                defaultValue={formModel.childrenDetails}
                readonly={readOnly}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="spouseDetails"
                label={Translation('prospect.spouse_details')}
                defaultValue={formModel.spouseDetails}
                readonly={readOnly}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="parentDetails"
                label={Translation('prospect.parent_details')}
                defaultValue={formModel.parentDetails}
                readonly={readOnly}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="otherFamilyDetails"
                label={Translation('prospect.other_family_details')}
                defaultValue={formModel.otherFamilyDetails}
                readonly={readOnly}
              />
            </Grid>
          </Grid>
        </div>
        <div className={styles.sectionBox}>
          <div className={styles.sectionTitle}>
            <span>{Translation('prospect.coverage_information')}</span>
          </div>
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6}>
              <InputItem
                name="existingCISumAssured"
                label={Translation('prospect.existing_sum_sssured')}
                defaultValue={formModel.existingCISumAssured}
                readonly={readOnly}
                filedError={filedErrors['existingCISumAssured']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="expectedIncomeCoverMonths"
                label={Translation('prospect.expected_income_cover_months')}
                defaultValue={formModel.expectedIncomeCoverMonths}
                readonly={readOnly}
                filedError={filedErrors['expectedIncomeCoverMonths']}
              />
            </Grid>
            <Grid item xs={6}>
              <InputItem
                name="preferredCISumAssured"
                label={Translation('prospect.preferred_sum_sssured')}
                defaultValue={formModel.preferredCISumAssured}
                readonly={readOnly}
              />
            </Grid>
          </Grid>
        </div>
        <div className={styles.sectionBox}>
          <div className={styles.sectionTitle}>
            <span>{Translation('prospect.category_history')}</span>
            <div style={{ marginTop: '24px' }}>
              <CategoryHistoryComponent updateLog={prospectDetail.categoryUpdatedLog ?? []} />
            </div>
          </div>
        </div>

        <Button onClick={deleteProspect} variant="outlined">
          {Translation('prospect.delete_prospect').toUpperCase()}
        </Button>
      </Form>
      <ReferredBy
        defaultSelectedRows={selectedReferrBy}
        open={showReferrby}
        onClose={() => setShowReferrby(false)}
        onConfirm={onSelectReferrBy}
      />
      <DeleteProspectDialog prospectDetail={prospectDetail} onClose={onDeleteDialogClose} open={showDeleteDialog} />
    </div>
  );
});
